import React, { useState, useCallback } from 'react';

/**
 * GpsWarningTag - Interactive "!GPS" badge that attempts to re-request
 * location access when tapped. If the browser denies permission (which
 * Chrome typically does after a prior denial), shows a modal explaining
 * how to re-enable location access in Chrome on Android.
 *
 * Props:
 *   onLocationUpdate - (optional) callback({lat, lng}) when GPS re-request succeeds
 *   setIsAccurate    - (optional) callback(bool) to update accuracy state
 */
const GpsWarningTag = ({ onLocationUpdate, setIsAccurate }) => {
  const [showHelpModal, setShowHelpModal] = useState(false);

  const handleTagPress = useCallback(() => {
    if (!navigator.geolocation) {
      setShowHelpModal(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        if (onLocationUpdate) {
          onLocationUpdate({ lat: latitude, lng: longitude });
        }
        if (setIsAccurate) {
          setIsAccurate(accuracy <= 100);
        }
      },
      (err) => {
        console.log('GPS re-request failed:', err.message);
        // Browser won't re-prompt after denial — show help modal
        setShowHelpModal(true);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [onLocationUpdate, setIsAccurate]);

  return (
    <>
      {/* Tappable !GPS badge */}
      <button
        onClick={handleTagPress}
        className="fixed top-16 left-0 mt-1 p-1 z-[1200] rounded-tr rounded-br bg-red-500 text-white opacity-60 active:opacity-90 cursor-pointer"
        aria-label="GPS unavailable — tap for help"
      >
        <span className="text-xs font-semibold">!GPS</span>
      </button>

      {/* Help modal — Chrome for Android instructions */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[2200]">
          <div className="bg-white p-5 rounded-lg w-11/12 max-w-sm text-center shadow-xl">
            <h2 className="text-lg font-bold mb-3 text-gray-800">Enable Location Access</h2>
            <p className="text-sm text-gray-600 mb-3">
              Location access was denied or is unavailable. To re-enable it in <strong>Chrome on Android</strong>:
            </p>
            <ol className="text-left text-sm text-gray-600 mb-4 space-y-2 pl-1">
              <li>1. Tap the <strong>lock icon</strong> (or tune icon) in Chrome's address bar</li>
              <li>2. Tap <strong>Permissions</strong> (or <strong>Site settings</strong>)</li>
              <li>3. Find <strong>Location</strong> and set it to <strong>Allow</strong></li>
              <li>4. Return to this page and <strong>refresh</strong></li>
            </ol>
            <p className="text-xs text-gray-400 mb-4">
              On iOS Safari, go to Settings → Safari → Location and select "Allow".
            </p>
            <button
              className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-5 py-2 rounded-lg text-sm"
              onClick={() => setShowHelpModal(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GpsWarningTag;
